import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../../firebase/firebase";
import EarningsMappingSection from "../EarningsMappingSection";
import { saveAndMergeMappings } from "../earningsSaver";
import { normalizeDailyBookingPortalRows, readDailyBookingPortalFile } from "./dailyBookingPortalParser";
import { saveDailyBookingPortalIncome } from "./dailyBookingPortalSaver";
import DailyBookingPortalUploadHistory from "./DailyBookingPortalUploadHistory";

const MAPPING_PLATFORM = "dailybookingportal";

const COMPANIES = [
  { value: "Comfort Line Luxury Motor Vehicles Services LLC", label: "Comfort Line" },
  { value: "Lolia Limousine for Luxury Motor Vehicles Services LLC", label: "Lolia Limousine" },
];

const money = (v) => Number(v || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DailyBookingPortalUpload() {
  const [company, setCompany] = useState("");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [existingMappings, setExistingMappings] = useState({});
  const [mappingUpdates, setMappingUpdates] = useState({});
  const [unmappedRows, setUnmappedRows] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "drivers"), (snap) => {
      setDriverList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "driverPlatformMappings", MAPPING_PLATFORM), (snap) => {
      setExistingMappings(snap.data() || {});
    });
    return () => unsub();
  }, []);

  const totals = useMemo(() => rows.reduce((acc, row) => {
    acc.bank += Number(row.bank_income || 0);
    acc.cash += Number(row.cash_income || 0);
    acc.dates.add(row.reportDate);
    return acc;
  }, { bank: 0, cash: 0, dates: new Set() }), [rows]);

  const dateRangeLabel = useMemo(() => {
    const dates = Array.from(totals.dates || []).sort();
    if (!dates.length) return "—";
    return dates.length === 1 ? dates[0] : `${dates[0]} to ${dates[dates.length - 1]}`;
  }, [totals]);

  const reset = () => {
    setRows([]);
    setInvalidRows([]);
    setUnmappedRows([]);
    setMappingUpdates({});
    setStatus("");
  };

  async function processFile() {
    if (!company) return alert("Select company first.");
    if (!file) return alert("Select the Daily Booking Portal Income file.");

    setProcessing(true);
    setStatus("");
    try {
      const rawRows = await readDailyBookingPortalFile(file);
      const parsed = normalizeDailyBookingPortalRows(rawRows);
      if (!parsed.rows.length) {
        throw new Error("No valid rows found. Required columns: Date, Driver Name, Bank Income, Cash Income.");
      }

      const mappingDoc = await getDoc(doc(db, "driverPlatformMappings", MAPPING_PLATFORM));
      const mappings = mappingDoc.exists() ? mappingDoc.data() || {} : {};
      const autoMappings = {};

      // Safe automatic match: only use an exact normalized saved-driver name.
      // Anything else remains visible for manual mapping instead of guessing.
      parsed.rows.forEach((row) => {
        if (mappings[row.driver_id]) return;
        const matches = driverList.filter((d) => {
          const full = String(d.fullName || d.name || "")
            .toLowerCase()
            .replace(/[^a-z0-9 ]/g, "")
            .replace(/\s+/g, " ")
            .trim()
            .split(" ")
            .filter((word) => !["mr", "mrs", "ms", "driver", "chauffeur", "sir"].includes(word))
            .slice(0, 3)
            .join(" ");
          return full && full === row.driver_id;
        });
        if (matches.length === 1) autoMappings[row.driver_id] = matches[0].id;
      });

      const mergedMappings = { ...mappings, ...autoMappings };
      if (Object.keys(autoMappings).length) {
        await saveAndMergeMappings(autoMappings, mappings, MAPPING_PLATFORM);
      }

      const missingMap = new Map();
      parsed.rows.forEach((row) => {
        if (!mergedMappings[row.driver_id] && !missingMap.has(row.driver_id)) {
          missingMap.set(row.driver_id, row);
        }
      });

      setExistingMappings(mergedMappings);
      setRows(parsed.rows);
      setInvalidRows(parsed.invalidRows);
      setUnmappedRows(Array.from(missingMap.values()));
      setStatus(
        `${parsed.rows.length} valid row(s) read across ${new Set(parsed.rows.map((r) => r.reportDate)).size} date(s).` +
        (missingMap.size ? ` Map ${missingMap.size} driver(s), then save.` : " Ready to save.")
      );
    } catch (err) {
      console.error(err);
      alert(`Booking Portal file could not be processed: ${err?.message || err}`);
    } finally {
      setProcessing(false);
    }
  }

  async function saveMappingsOnly() {
    const missingSelections = unmappedRows.filter((r) => !mappingUpdates[r.driver_id]);
    if (missingSelections.length) {
      alert("Please map every listed driver before continuing.");
      return;
    }

    try {
      const merged = await saveAndMergeMappings(mappingUpdates, existingMappings, MAPPING_PLATFORM);
      setExistingMappings(merged);
      setUnmappedRows([]);
      setMappingUpdates({});
      setStatus(`${rows.length} valid row(s) ready to save.`);
      alert("Booking Portal driver mappings saved.");
    } catch (err) {
      console.error(err);
      alert(`Could not save driver mappings: ${err?.message || err}`);
    }
  }

  async function saveReport() {
    if (!company) return alert("Select company.");
    if (!file || !rows.length) return alert("Process a Booking Portal file first.");
    if (unmappedRows.length) return alert("Map all drivers before saving.");

    setSaving(true);
    try {
      const results = await saveDailyBookingPortalIncome({
        rows,
        company,
        mappings: existingMappings,
        sourceFileName: file.name,
        uploadedBy: auth.currentUser?.email || "",
      });

      const saved = results.reduce((a, x) => a + Number(x.savedCount || 0), 0);
      const bank = results.reduce((a, x) => a + Number(x.bankTotal || 0), 0);
      const cash = results.reduce((a, x) => a + Number(x.cashTotal || 0), 0);
      const replaced = results.reduce((a, x) => a + Number(x.replacedCount || 0), 0);

      setStatus(`Saved ${saved} row(s). Booking Portal Bank AED ${money(bank)} / Booking Portal Cash AED ${money(cash)}.`);
      alert(
        `Daily Booking Portal income saved.\n` +
        `Dates: ${dateRangeLabel}\n` +
        `Rows: ${saved}\n` +
        `Booking Portal Bank: AED ${money(bank)}\n` +
        `Booking Portal Cash: AED ${money(cash)}\n` +
        (replaced ? `Replaced previous rows: ${replaced}` : "")
      );
    } catch (err) {
      console.error(err);
      alert(`Booking Portal save failed: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: 22, maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0 }}>Daily Booking Portal Income Upload</h2>

      <div style={{ padding: 14, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, marginBottom: 18 }}>
        <strong>Daily Income Report only.</strong> This upload is stored in separate Daily Booking Portal collections and is <strong>not</strong> used by Car Sheets, monthly Manual Booking uploads, or client invoicing.
        Required columns: <strong>Date, Driver Name, Bank Income, Cash Income</strong>.
      </div>

      <div style={grid}>
        <label style={fieldLabel}>
          Income Company
          <select value={company} onChange={(e) => { setCompany(e.target.value); reset(); }} style={input}>
            <option value="">Select company</option>
            {COMPANIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        <label style={fieldLabel}>
          Booking Portal file
          <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); reset(); }} style={input} />
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button onClick={processFile} disabled={processing} style={button("#2563eb")}>
          {processing ? "Processing..." : "Process Booking Portal File"}
        </button>
        <button onClick={saveReport} disabled={saving || !rows.length || !!unmappedRows.length} style={button("#15803d")}>
          {saving ? "Saving..." : "Save Daily Booking Portal Income"}
        </button>
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: 18, padding: 14, border: "1px solid #ddd", borderRadius: 8, background: "#fff" }}>
          <strong>Preview:</strong> {dateRangeLabel} — {rows.length} row(s)
          <div style={{ marginTop: 8 }}>
            Booking Portal Bank: <strong>AED {money(totals.bank)}</strong> &nbsp; | &nbsp;
            Booking Portal Cash: <strong>AED {money(totals.cash)}</strong>
          </div>
          {invalidRows.length > 0 && (
            <div style={{ marginTop: 8, color: "#b45309" }}>
              Skipped {invalidRows.length} row(s) with missing/invalid Date or Driver Name.
            </div>
          )}
          {status && <div style={{ marginTop: 8, color: "#475569" }}>{status}</div>}
        </div>
      )}

      <EarningsMappingSection
        unmappedRows={unmappedRows}
        driverList={driverList}
        mappingUpdates={mappingUpdates}
        setMappingUpdates={setMappingUpdates}
        onSaveMapping={saveMappingsOnly}
      />

      <DailyBookingPortalUploadHistory company={company} />
    </div>
  );
}

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 };
const fieldLabel = { display: "flex", flexDirection: "column", gap: 6, fontWeight: 700 };
const input = { padding: 9, border: "1px solid #cbd5e1", borderRadius: 6, background: "white" };
const button = (background) => ({ padding: "10px 16px", border: "none", borderRadius: 6, color: "white", background, fontWeight: 700, cursor: "pointer" });
